import { i18n } from '@/i18n';

import { AuthGuard } from '@/auth/auth-guards';
import { invitationsMessages } from '@/components/Activations/Layout/ActivationTabsMessages';
import { activationRoutes } from '@/views/activation/activation-routes';
import { adminRoutes } from '@/views/admin/admin-routes';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { getRoute, RoutePaths } from '@/views/gallery/route-paths';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';
import { sideNavMessages } from '@/views/gallery/side-nav.messages';
import { inventoryRoutes } from '@/views/inventory/inventory-routes';
import { Permission, Subscription } from '@art-basel/artbasel-lib-platform';
import type { RouteLocation, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { linkInBioRoutes } from '../linkInBio/link-in-bio-routes';
import { salesAgreementRoutes } from './sales-agreement/salesAgreement-routes';
import { artistProfileMessages, artistsMessages, editArtistMessages } from './artists/artists-messages.ts';
import { useAuthorizationStore } from '@/stores/AuthorizationStore/AuthorizationStore.ts';

const { t } = i18n.global;

const getGalleryProfileChildPath = (path: string) => {
  const profileRoot = `${RoutePaths.GALLERY_PROFILE.PROFILE}/`;
  return path.startsWith(profileRoot) ? path.replace(profileRoot, '') : path;
};

export const backOfficeRoutes: Array<RouteRecordRaw> = [
  {
    meta: { title: t('platform.app.title') },
    path: RoutePaths.ROOT,
    component: () => import(`./Home.vue`),
    children: [
      {
        meta: { title: invitationsMessages.dashboardTitle },
        path: '',
        redirect: getRoute(RoutePaths.DASHBOARD),
      },
      {
        meta: { title: invitationsMessages.dashboardTitle },
        path: RoutePaths.DASHBOARD,
        component: () => import(`./invitations/InvitationsDashboard.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      {
        meta: { title: invitationsMessages.artists },
        path: RoutePaths.ARTISTS,
        component: () => import(`./artists/listing/ArtistsListing.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      {
        meta: { title: artistsMessages.newArtist },
        path: RoutePaths.ARTISTS_CREATE,
        component: () => import(`./artists/create/CreateNewArtist.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      {
        meta: { title: artistProfileMessages.pageTitleStatic, isArtistProfileRoute: true },
        path: RoutePaths.ARTISTS_DETAIL,
        component: () => import(`./artists/profile/ArtistProfile.vue`),
        beforeEnter: async (to: RouteLocation | RouteLocationNormalized) => {
          const authResult = await AuthGuard(to);
          if (authResult !== true) return authResult;
          // TODO - Create a guard for feature flags to avoid repeating this logic in every route
          const { featureFlags } = useAmplitudeFeatureFlags();
          if (!featureFlags.value.isArtistsEnabled) {
            return getRoute(RoutePaths.DASHBOARD);
          }
          return true;
        },
        children: [
          {
            meta: { title: artistProfileMessages.pageTitleStatic, isArtistProfileRoute: true },
            path: RoutePaths.ARTISTS_DETAIL_GALLERY,
            component: () => import(`./artists/profile/ArtistProfile.vue`),
          },
          {
            meta: { title: artistProfileMessages.pageTitleStatic, isArtistProfileRoute: true },
            path: RoutePaths.ARTISTS_DETAIL_ARTWORK,
            component: () => import(`./artists/profile/ArtistProfile.vue`),
          },
        ],
      },
      {
        meta: { title: editArtistMessages.documentTitle },
        path: RoutePaths.ARTISTS_EDIT,
        component: () => import(`./artists/edit/EditArtist.vue`),
        beforeEnter: async (to: RouteLocation | RouteLocationNormalized) => {
          const authResult = await AuthGuard(to);
          if (authResult !== true) return authResult;
          const { featureFlags } = useAmplitudeFeatureFlags();
          if (!featureFlags.value.isArtistsEnabled) {
            return getRoute(RoutePaths.DASHBOARD);
          }
          return true;
        },
      },
      {
        meta: { title: invitationsMessages.detailsTitle },
        path: RoutePaths.INVITATIONS,
        component: () => import(`./invitations/InvitationDetails.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      ...inventoryRoutes,
      ...activationRoutes,
      ...linkInBioRoutes,
      ...adminRoutes,
      {
        meta: { title: backOfficeMessages.sellerPurchaseRequestsTitle },
        path: RoutePaths.SELL.REQUESTS,
        component: () => import(`./purchase-requests/purchase-request-lists/PurchaseRequestsSellerList.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to, Permission.READ_PURCHASE_REQUESTS),
      },
      {
        meta: { title: t('platform.app.purchaseRequest.purchaseRequestDetailTitle') },
        path: RoutePaths.SELL.REQUEST_DETAIL,
        component: () => import(`./purchase-requests/PurchaseRequestDetails.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to, Permission.READ_PURCHASE_REQUESTS),
      },
      {
        meta: { title: backOfficeMessages.buyerPurchaseRequestsTitle },
        path: RoutePaths.BUY.REQUESTS,
        component: () => import(`./purchase-requests/purchase-request-lists/PurchaseRequestsBuyerList.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      ...salesAgreementRoutes,
      {
        meta: { title: sideNavMessages.account },
        path: RoutePaths.ACCOUNT.ROOT,
        children: [
          {
            path: '',
            redirect: getRoute(RoutePaths.ACCOUNT.SETTINGS),
          },
          {
            meta: { title: sideNavMessages.accountSettings },
            path: RoutePaths.SETTINGS,
            beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
            component: () => import('./account/settings/AccountSettings.vue'),
          },
          {
            meta: { title: sideNavMessages.mySettings },
            path: RoutePaths.MY_SETTINGS,
            beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
            component: () => import('./account/settings/MySettings.vue'),
          },
          {
            meta: { title: sideNavMessages.subscription },
            path: RoutePaths.SUBSCRIPTION,
            beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
            component: () => import('./account/subscription/Subscription.vue'),
          },
          {
            meta: { title: sideNavMessages.accountPreferences },
            path: RoutePaths.PREFERENCES,
            beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
            component: () => import('./account/preferences/Preferences.vue'),
          },
          {
            meta: { title: sideNavMessages.accountTeam },
            path: 'team',
            component: () => import('./account/team/Team.vue'),
            beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to, Permission.READ_TEAM),
          },
        ],
      },
      {
        meta: { title: t('platform.component.paymentOnboarding.title') },
        path: RoutePaths.PAYMENT_ONBOARDING,
        component: () => import(`./payment/PaymentOnboarding.vue`),
        beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
      },
      {
        meta: { title: backOfficeMessages.organizations },
        path: RoutePaths.ORGANIZATIONS,
        component: () => import(`./organizations/Organizations.vue`),
        beforeEnter: [
          (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
          () => {
            // TODO - Create a guard for feature flags to avoid repeating this logic in every route
            const { featureFlags } = useAmplitudeFeatureFlags();
            if (!featureFlags.value.isGalleryProfileEnabled) {
              return getRoute(RoutePaths.DASHBOARD);
            }
            return true;
          },
          () => {
            // TODO - We should also create a guard for subscription checks to avoid repeating this logic in every route
            const { hasSubscription } = useAuthorizationStore();
            if (!hasSubscription(Subscription.ENTERPRISE)) {
              return getRoute(RoutePaths.DASHBOARD);
            }
            return true;
          },
        ],
      },
      {
        meta: { title: backOfficeMessages.galleryProfile },
        path: RoutePaths.GALLERY_PROFILE.PROFILE,
        component: () => import(`./gallery-profile/GalleryProfile.vue`),
        beforeEnter: [
          (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
          () => {
            // TODO - Create a guard for feature flags to avoid repeating this logic in every route
            const { featureFlags } = useAmplitudeFeatureFlags();
            if (!featureFlags.value.isGalleryProfileEnabled) {
              return getRoute(RoutePaths.DASHBOARD);
            }
            return true;
          },
        ],
        children: [
          {
            meta: { title: backOfficeMessages.galleryProfile },
            path: '',
            component: () => import(`./gallery-profile/profile/GalleryProfileProfile.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileExhibitions },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.EXHIBITIONS),
            component: () => import(`./gallery-profile/exhibitions/GalleryProfileExhibitions.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileFairs },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.FAIRS),
            component: () => import(`./gallery-profile/fairs/GalleryProfileFairs.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileArtists },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.ARTISTS),
            component: () => import(`./gallery-profile/artists/GalleryProfileArtists.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileTeam },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.TEAM),
            component: () => import(`./gallery-profile/team/GalleryProfileTeam.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileLocations },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.LOCATIONS),
            component: () => import(`./gallery-profile/locations/GalleryProfileLocations.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfileBilling },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.BILLING),
            component: () => import(`./gallery-profile/billing/GalleryProfileBilling.vue`),
          },
          {
            meta: { title: backOfficeMessages.galleryProfilePress },
            path: getGalleryProfileChildPath(RoutePaths.GALLERY_PROFILE.PRESS),
            component: () => import(`./gallery-profile/press/GalleryProfilePress.vue`),
          },
        ],
      },
      // TODO: Remove legacy route redirects Q3 2025
      {
        meta: { title: backOfficeMessages.artworksTitle },
        path: 'artworks',
        redirect: getRoute(RoutePaths.INVENTORY),
      },
      {
        meta: { title: backOfficeMessages.artworkCreateTitle },
        path: 'artworks/create',
        redirect: getRoute(RoutePaths.INVENTORY_CREATE),
      },
      {
        meta: { title: backOfficeMessages.artworkDetailTitle },
        path: 'artworks/:id',
        redirect: (to) => getRoute(RoutePaths.INVENTORY_DETAIL, to.params.id as string | undefined),
      },
      {
        meta: { title: backOfficeMessages.artworkEditTitle },
        path: 'artworks/:id/edit',
        redirect: (to) => getRoute(RoutePaths.INVENTORY_EDIT, to.params.id as string | undefined),
      },
    ],
  },
];
